import { useState } from 'react'
import { toast } from 'react-toastify'
import { changePassword, sendCode, verifyCode, reset } from '../features/passwordReset/passwordResetSlice'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { FiEye, FiEyeOff } from 'react-icons/fi'

function PasswordReset() {
  const [phoneValue, setphoneValue] = useState('');
  const [codeValue, setCodeValue] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const verification = useSelector(state => state.passwordReset.verification)
  const isSuccess = useSelector(state => state.passwordReset.isSuccess)

  const handleInputChangePhoneBox = (event) => {
    const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, 10);
    setphoneValue(digitsOnly);
  };

  const handleInputChangeCodeBox = (event) => {
    const newValue = event.target.value;
    if (/^\d{0,6}$/.test(newValue)) {
      setCodeValue(newValue);
    }
  };

  const handleSubmitPhone = (event) => {
    event.preventDefault();
    const cleanedPhone = phoneValue.replace(/\D/g, '');
    dispatch(sendCode(cleanedPhone))
    toast.info('If registered, you will receive a code via text message')
  };

  const handleSubmitCode = (event) => {
    event.preventDefault();
    const cleanedPhone = phoneValue.replace(/\D/g, '');
    const data = { phone: cleanedPhone, code: codeValue }
    dispatch(verifyCode(data))
    toast.info('Code sent')
  }

  const [password1Value, setPassword1Value] = useState('')
  const [password2Value, setPassword2Value] = useState('')
  const [showPassword1, setShowPassword1] = useState(false)
  const [showPassword2, setShowPassword2] = useState(false)
  const [isResetPasswordClicked, setIsResetPasswordClicked] = useState(false);

  const handlePassword1Change = (event) => { setPassword1Value(event.target.value); };
  const handlePassword2Change = (event) => { setPassword2Value(event.target.value); };

  const handleResetPasswordClick = (event) => {
    event.preventDefault();
    setIsResetPasswordClicked(true);
    const isValidPassword = validatePasswords(password1Value, password2Value)
    if (!isValidPassword) {
      toast.error('New Password is not secure. Please try again')
    } else {
      setIsResetPasswordClicked(false);
      const dataNewPassword = { token: verification.token, password: password1Value }
      dispatch(changePassword(dataNewPassword))
      dispatch(reset())
      navigate('/login');
      toast.success('Password has changed.')
    }
  };

  const validatePasswords = (password1, password2) => {
    const upperCaseRegex = /[A-Z]/;
    const lowerCaseRegex = /[a-z]/;
    const digitCaseRegex = /\d/;
    if (
      password1.length < 8 ||
      password1 !== password2 ||
      !upperCaseRegex.test(password1) ||
      !lowerCaseRegex.test(password1) ||
      !digitCaseRegex.test(password1)
    ) {
      return false;
    }
    return true;
  };

  return (
    <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-4 py-12">
      <h1 className="text-card-title text-ink font-mono font-semibold text-center mb-8">
        Reset Password
      </h1>

      <form
        onSubmit={handleSubmitPhone}
        className="max-w-md w-full mx-auto p-6 rounded-xl bg-surface border border-border shadow-sm mb-4"
      >
        <div className="flex flex-col gap-3">
          <label className="text-label text-ink" htmlFor="phoneValue">
            Business phone
          </label>
          <input
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-body text-ink placeholder:text-ink-4 focus:outline-none focus:ring-2 focus:ring-brand/40"
            type="text"
            id="phoneValue"
            required
            value={
              phoneValue.length <= 3
                ? phoneValue
                : phoneValue.length <= 6
                ? `${phoneValue.slice(0, 3)}-${phoneValue.slice(3)}`
                : `${phoneValue.slice(0, 3)}-${phoneValue.slice(3, 6)}-${phoneValue.slice(6)}`
            }
            onChange={handleInputChangePhoneBox}
          />
          <button
            className="bg-ink text-white rounded-lg text-label font-medium px-4 py-2 hover:bg-ink-2 transition"
            type="submit"
          >
            Get reset code
          </button>
        </div>
      </form>

      <form
        onSubmit={handleSubmitCode}
        className="max-w-md w-full mx-auto p-6 rounded-xl bg-surface border border-border shadow-sm"
      >
        <div className="flex flex-col gap-3">
          <label className="text-label text-ink" htmlFor="codeValue">
            Enter code
          </label>
          <input
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-body text-ink placeholder:text-ink-4 focus:outline-none focus:ring-2 focus:ring-brand/40"
            type="text"
            id="codeValue"
            pattern="\d{6}"
            required
            value={codeValue}
            onChange={handleInputChangeCodeBox}
          />
          <button
            className="bg-ink text-white rounded-lg text-label font-medium px-4 py-2 hover:bg-ink-2 transition"
            type="submit"
          >
            Verify Code
          </button>
        </div>
      </form>

      {/* New password modal — shown after code is verified */}
      <div className={`fixed inset-0 bg-black/80 flex items-center justify-center ${
          verification.status === 'approved' && isSuccess ? '' : 'hidden'
        }`}
      >
        <form onSubmit={handleResetPasswordClick} className="w-full max-w-lg px-4">
          <div className="bg-surface border border-border p-6 rounded-xl shadow-md">
            <h2 className="text-section text-ink font-semibold mb-4 text-center">Set New Password</h2>

            <div className="flex flex-col space-y-4">
              <div className="relative">
                <input
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 pr-10 text-body text-ink placeholder:text-ink-4 focus:outline-none focus:ring-2 focus:ring-brand/40"
                  type={showPassword1 ? 'text' : 'password'}
                  id="password1Value"
                  required
                  placeholder="Enter new password"
                  value={password1Value}
                  onChange={handlePassword1Change}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-3 flex items-center text-ink-3"
                  onClick={() => setShowPassword1((prev) => !prev)}
                >
                  {showPassword1 ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>

              <div className="relative">
                <input
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 pr-10 text-body text-ink placeholder:text-ink-4 focus:outline-none focus:ring-2 focus:ring-brand/40"
                  type={showPassword2 ? 'text' : 'password'}
                  id="password2Value"
                  required
                  placeholder="Confirm new password"
                  value={password2Value}
                  onChange={handlePassword2Change}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-3 flex items-center text-ink-3"
                  onClick={() => setShowPassword2((prev) => !prev)}
                >
                  {showPassword2 ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            {(() => {
              const hasMinLength = password1Value.length >= 8;
              const hasDigit = /\d/.test(password1Value);
              const hasUpper = /[A-Z]/.test(password1Value);
              const hasLower = /[a-z]/.test(password1Value);
              const hasUpperAndLower = hasUpper && hasLower;
              const reqClass = (ok) => ok ? 'text-active' : 'text-ink-4';
              return (
                <div className="mt-4 text-center">
                  <h3 className="text-label text-ink font-medium mb-2">New password must contain:</h3>
                  <ul className="space-y-1 text-body">
                    <li className={reqClass(hasMinLength)}>
                      <span className="mr-1">{hasMinLength ? '✓' : '•'}</span>
                      8 characters or more
                    </li>
                    <li className={reqClass(hasDigit)}>
                      <span className="mr-1">{hasDigit ? '✓' : '•'}</span>
                      At least one digit
                    </li>
                    <li className={reqClass(hasUpperAndLower)}>
                      <span className="mr-1">{hasUpperAndLower ? '✓' : '•'}</span>
                      At least one uppercase and one lowercase letter
                    </li>
                  </ul>
                </div>
              );
            })()}

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-border text-ink-2 hover:bg-surface-2 transition text-label"
                onClick={() => {
                  setPassword1Value('');
                  setPassword2Value('');
                  setShowPassword1(false);
                  setShowPassword2(false);
                  dispatch(reset());
                }}
              >
                Cancel
              </button>
              <button
                className="bg-ink hover:bg-ink-2 text-white text-label font-medium py-2 px-4 rounded-lg transition"
                type="submit"
              >
                Reset Password
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PasswordReset
